import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VideoCall from './VideoCall';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { onPrivateMessageReceived, socket as globalSocket } from '../../Socket/socket';

const VideoCallPageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  overflow: hidden;
  box-sizing: border-box;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  color: #a5b4fc;
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  color: white;
  text-align: center;
  padding: 20px;
`;

const ErrorButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(99, 102, 241, 0.95) 100%);
  color: white;
  border: 1px solid rgba(165, 180, 252, 0.3);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.95) 0%, rgba(147, 51, 234, 0.95) 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
  }
`;

const DebugInfo = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 15px;
  border-radius: 8px;
  margin: 10px 0;
  font-family: monospace;
  font-size: 12px;
  text-align: left;
  max-width: 500px;
  color: #c7d2fe;
`;

const IncomingMessageBanner = styled.div`
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(99, 102, 241, 0.95) 100%);
  backdrop-filter: blur(8px);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  z-index: 10000;
  border: 1px solid rgba(165, 180, 252, 0.4);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const VideoCallPage = ({ conversation, sendUserCallStarted }) => {
  const { expertId, channelName } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const [callConfig, setCallConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state to avoid flash of error
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const authenticatedAxios = useAuthenticatedAxios();
  const [incomingMessage, setIncomingMessage] = useState(null);

  useEffect(() => {
    const initializeCall = async () => {
      try {
        setError(null);

        const debug = {
          expertId: expertId,
          channelName: channelName,
          isAuthenticated,
          agoraAppId: process.env.REACT_APP_AGORA_APP_ID,
          userAgent: navigator.userAgent,
          hasMediaDevices: !!navigator.mediaDevices,
          hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia
        };
        setDebugInfo(debug);

        if (!expertId || !channelName) {
          setError('Missing required parameters: expertId and channelName');
          setIsLoading(false);
          return;
        }

        if (!process.env.REACT_APP_AGORA_APP_ID) {
          setError('Missing Agora App ID. Please check your environment variables.');
          setIsLoading(false);
          return;
        }

        // Get call configuration from server
        const response = await authenticatedAxios.post(`/video-call/join-room-expert`, {
          conversationId: channelName,
          userId: expertId,
          channelName: channelName
        });

        if (response.status !== 200) {
          throw new Error('Failed to get call configuration');
        }

        const config = {
          appId: process.env.REACT_APP_AGORA_APP_ID,
          channelName: channelName,
          token: response.data.data.token,
          uid: response.data.data.uid,
          expertName: response.data.data.expert.name,
          userName: response.data.data.user.name
        };

        setCallConfig(config);
        setIsLoading(false);

      } catch (err) {
        console.error('Error initializing video call:', err);
        setError(`Failed to initialize expert video call: ${err.message}`);
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      initializeCall();
    } else {
      setIsLoading(false);
      setError('Authentication required');
    }
  }, []);

  // Listen for incoming private messages while on the video call page
  useEffect(() => {
    const unsubscribe = onPrivateMessageReceived((data) => {
      // Show a lightweight in-call banner and log for diagnostics
      setIncomingMessage({ sender: data.username, content: data.content, threadId: data.threadId, time: new Date().toISOString() });
      console.log('🔵 [VideoCallPage] Incoming message during call:', data);
    });
    return () => {
      unsubscribe && unsubscribe();
    };
  }, []);

  const handleCallEnd = (duration) => {
    console.log(`Expert call ended after ${duration} seconds`);
    navigate(-1);
  };

  const handleError = (error) => {
    console.error('Video call error:', error);
    setError(`Expert video call encountered an error: ${error.message || error}`);
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Re-initialize the call
    setTimeout(() => {
      setIsLoading(false);
      setCallConfig(null);
    }, 1000);
  };

  const handleTestMode = () => {
    // Test mode - bypass actual video call
    setError(null);
    setIsLoading(false);
    setCallConfig({
      appId: 'test-app-id',
      channelName: 'test-channel',
      token: 'test-token',
      uid: 1234567890,
      expertName: 'Test Expert'
    });
  };

  const createRoom = async () => {
    // try {
    //   const user = conversation.users.find(user => user.type === 'user');
    //   const response = await authenticatedAxios.post('/video-call/create-room', {
    //     conversationId: channelName,
    //     userId: user.id
    //   });

    //   // Assuming response.data contains expertId and channelName
    //   // setRoomExpertId(response.data.expertId); // This line is removed
    //   // setRoomChannelName(response.data._id); // This line is removed
    //   return response.data;
    // } catch (err) {
    //   console.error('Error creating room:', err);
    //   throw err;
    // }
  };

  if (!isActive) {
    return (
      <ErrorContainer>
        <h2>Expert Call Not Active</h2>
        <p>Please generate a call to start the video call.</p>
        <ErrorButton onClick={() => createRoom()}>
          Generate Call
        </ErrorButton>
      </ErrorContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <ErrorContainer>
        <h2>Expert Authentication Required</h2>
        <p>Please log in as an expert to access the video call feature.</p>
        <ErrorButton onClick={() => navigate('/')}>
          Go to Home
        </ErrorButton>
      </ErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        Initializing expert video call...
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <h2>Expert Call Error</h2>
        <p>{error}</p>

        <DebugInfo>
          <strong>Expert Debug Information:</strong><br />
          Expert ID: {debugInfo.expertId}<br />
          Channel Name: {debugInfo.channelName}<br />
          Authenticated: {debugInfo.isAuthenticated ? 'Yes' : 'No'}<br />
          Agora App ID: {debugInfo.agoraAppId ? 'Set' : 'Missing'}<br />
          Media Devices: {debugInfo.hasMediaDevices ? 'Available' : 'Not Available'}<br />
          GetUserMedia: {debugInfo.hasGetUserMedia ? 'Available' : 'Not Available'}<br />
          Socket Connected: {globalSocket && globalSocket.connected ? 'Yes' : 'No'}
        </DebugInfo>

        <div style={{ display: 'flex', gap: '10px' }}>
          <ErrorButton onClick={handleRetry}>
            Retry
          </ErrorButton>
          <ErrorButton onClick={handleTestMode}>
            Test Mode
          </ErrorButton>
          <ErrorButton onClick={() => navigate(-1)}>
            Go Back
          </ErrorButton>
        </div>
      </ErrorContainer>
    );
  }

  if (callConfig) {
    return (
      <VideoCallPageContainer>
        {incomingMessage && (
          <IncomingMessageBanner>
            New message from {incomingMessage.sender || 'Someone'}
          </IncomingMessageBanner>
        )}
        <VideoCall
          appId={callConfig.appId}
          channelName={callConfig.channelName}
          token={callConfig.token}
          uid={callConfig.uid}
          userName={callConfig.userName}
          onCallEnd={handleCallEnd}
          onError={handleError}
          authenticatedAxios={authenticatedAxios}
        />
      </VideoCallPageContainer>
    );
  }

  return (
    <ErrorContainer>
      <h2>Unable to Start Expert Call</h2>
      <p>Please check your expert configuration and try again.</p>
      <ErrorButton onClick={() => navigate(-1)}>
        Go Back
      </ErrorButton>
    </ErrorContainer>
  );
};

export default VideoCallPage; 